import { PRODUCT_URLS } from '@podus/utils';
import { ScrollReveal } from '@podus/ui';

const platforms = [
    { icon: '/icons/desktop.svg', label: 'Desktop' },
    { icon: '/icons/microsoft.svg', label: 'Microsoft' },
    { icon: '/icons/browser.svg', label: 'Browser' },
    { icon: '/icons/whatsapp.svg', label: 'Whatsapp' },
    { icon: '/icons/applestore.svg', label: 'App Store' },
    { icon: '/icons/playstore.svg', label: 'Play Store' },
];

export default function Hero() {
    return (
        <section className="px-4 pt-10 lg:px-16 lg:pt-16 xl:px-20">
            <div className="relative flex flex-col lg:flex-row lg:gap-10 xl:gap-16">
                {/* ── Left column ── */}
                <div className="flex flex-col lg:w-[60%] lg:shrink-0 lg:pb-30">
                    {/* Heading */}
                    <ScrollReveal variant="fade-up" duration={800}>
                        <h1>
                            <span className="block font-bold text-[clamp(2.8rem,7vw,6rem)] leading-[1] text-[#AEAEAE]">
                                AI-Powered
                            </span>
                            <span className="block text-[clamp(3.2rem,9vw,7.5rem)] leading-[1.1] font-normal italic text-secondary">
                                Legal Assistant
                            </span>
                        </h1>
                    </ScrollReveal>

                    {/* Arrow + Description row */}
                    <ScrollReveal variant="fade-up" delay={200} duration={700}>
                        <div className="mt-10 flex flex-col gap-1 sm:mt-14 sm:flex-row sm:items-start sm:gap-3 lg:mt-16">
                            <span className="text-xl leading-none text-primary">&rarr;</span>
                            <p className="max-w-[550px] text-[16px] leading-[1.6] text-[#434343] sm:leading-loose">
                                Get instant legal guidance, draft documents, and understand your rights within minutes.
                                Podus AI helps you navigate legal issues confidently — anytime, anywhere.
                            </p>
                        </div>
                    </ScrollReveal>

                    {/* Mobile: blurb + stats */}
                    <ScrollReveal variant="fade-up" delay={300} className="lg:hidden">
                        <div>
                            <p className="mt-6 text-[15px] leading-[1.6] sm:leading-loose text-[#747474]">
                                Get fast, reliable legal guidance powered by AI — anytime, anywhere.
                            </p>
                            <div className="mt-8">
                                <div className="flex items-baseline gap-0.5">
                                    <span className="text-[40px] font-bold leading-none text-secondary">33K</span>
                                    <span className="text-[32px] font-bold leading-none text-primary">+</span>
                                </div>
                                <p className="mt-1.5 text-[11px] text-[#747474]">Users Already Onboard</p>
                                <div className="mt-3 flex -space-x-2.5">
                                    {[1, 2, 3, 4].map((n) => (
                                        <img
                                            key={n}
                                            src={`/assets/avatar_${n}.png`}
                                            alt=""
                                            className="h-10 w-10 rounded-full border-2 border-white object-cover"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </ScrollReveal>

                    {/* Get Started button */}
                    <ScrollReveal variant="fade-up" delay={400}>
                        <a
                            href={PRODUCT_URLS.chatpodusai}
                            className="mt-8 flex w-full items-center justify-center rounded-full bg-secondary px-10 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98] min-[400px]:mt-10 min-[400px]:px-14 min-[400px]:py-4 min-[400px]:text-[15px] sm:w-fit lg:mt-12"
                        >
                            Get Started
                        </a>
                    </ScrollReveal>

                    {/* Hero image — mobile */}
                    <ScrollReveal
                        variant="fade-up"
                        delay={400}
                        className="relative mt-10 flex items-end justify-center lg:hidden"
                    >
                        <img
                            src="/assets/law_woman.png"
                            alt="AI-powered legal assistant illustration"
                            className="h-auto w-full max-w-xs object-contain sm:max-w-sm"
                        />
                    </ScrollReveal>

                    {/* Gradient fade — overlaps bottom of hero image on mobile */}
                    <div className="pointer-events-none relative z-20 -mt-90 bottom-[-48px] h-52 bg-linear-to-b from-transparent via-white/60 to-white lg:hidden" />

                    {/* Download card */}
                    <div style={{ zIndex: 100 }}>
                        <div className="download-card relative z-10 mx-auto w-full max-w-130 p-5 pb-6 pt-10 min-[400px]:p-6 min-[400px]:pb-7 min-[400px]:pt-12 sm:mt-16 sm:px-5 sm:pt-8 sm:pb-6 lg:mx-0 lg:mt-16.25">
                            <div className="relative mb-5 flex items-center gap-3 min-[400px]:mb-6 min-[400px]:gap-4 sm:mb-5 sm:gap-3">
                                <img
                                    src="/assets/team_image_small.png"
                                    alt="Team"
                                    className="h-20 w-24 rounded-xl object-cover min-[400px]:h-24 min-[400px]:w-28 min-[400px]:rounded-2xl sm:h-20 sm:w-24 sm:rounded-xl"
                                />
                                <div className="relative">
                                    <p className="text-sm text-[#747474] min-[400px]:text-base sm:text-sm">
                                        Download Now
                                    </p>
                                    <p className="text-xl font-bold text-secondary min-[400px]:text-2xl sm:text-2xl">
                                        Available on:
                                    </p>
                                </div>
                                <span className="absolute right-[10%] top-3 h-3 w-3 rounded-full bg-primary min-[400px]:h-3.5 min-[400px]:w-3.5 sm:right-[40%] sm:top-1 sm:h-3 sm:w-3" />
                            </div>
                            <div className="relative grid grid-cols-3 gap-3 min-[400px]:gap-3.5 sm:grid-cols-6">
                                {platforms.map((p) => (
                                    <div key={p.label} className="flex flex-col items-center gap-1.5 min-[400px]:gap-2">
                                        <div className="flex h-20 w-full items-center justify-center rounded-xl bg-[#e0e0e0]/50 min-[400px]:h-24 min-[400px]:rounded-2xl sm:h-14 sm:w-14 sm:rounded-xl">
                                            <img
                                                src={p.icon}
                                                alt={p.label}
                                                className="h-7 w-7 object-contain min-[400px]:h-9 min-[400px]:w-9 sm:h-6 sm:w-6"
                                            />
                                        </div>
                                        <span className="max-w-full truncate text-xs text-secondary-300 min-[400px]:text-sm sm:text-xs lg:text-sm">
                                            {p.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right column ── */}
                <div className="relative mt-10 flex flex-1 flex-col lg:mt-0">
                    {/* Blurb + Stats — desktop only */}
                    <ScrollReveal variant="fade-left" delay={300} distance={30} className="mt-[50px] hidden lg:block">
                        <div>
                            <div className="flex items-start gap-2.5">
                                <span className="mt-0.5 text-lg leading-none text-primary">&rarr;</span>
                                <p className="max-w-[440px] text-[16px] leading-[1.65] text-[#747474]">
                                    Get fast, reliable legal guidance powered by AI — anytime, anywhere.
                                </p>
                            </div>
                            <div className="mt-8 flex items-center gap-6">
                                <div>
                                    <div className="flex items-baseline gap-0.5">
                                        <span className="text-[40px] font-bold leading-none text-secondary">33K</span>
                                        <span className="text-[32px] font-bold leading-none text-primary">+</span>
                                    </div>
                                    <p className="mt-1.5 text-[14px] text-[#747474]">Users Already Onboard</p>
                                </div>
                                <div className="flex -space-x-2.5">
                                    {[1, 2, 3, 4].map((n) => (
                                        <img
                                            key={n}
                                            src={`/assets/avatar_${n}.png`}
                                            alt=""
                                            className="h-15 w-15 rounded-full border-2 border-white object-cover"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </ScrollReveal>
                </div>
            </div>
        </section>
    );
}
